// M8-ASSESS: the round report, deterministic from the engine (x-ray.md §8 rule 1: the
// deterministic layer is the floor; no model writes a number). `buildRoundReport` runs the lifted
// team aggregation over a round's respondents and shapes what the report pages show:
//
// - team: hours claimed against capacity, the team's energy, how many are over or under loaded;
// - roles: one card per respondent (load, energy, fit, clarity, the seat-health colour of
//   `teamWorkChart.deriveNodeHealth`, adapted: x-ray.md §2.4) and "didn't take part" for the
//   rest (rule 2: missing is not zero);
// - gaps: low participation first (rule 3), then overload, cracks named by several people,
//   duplicated work, manager-vs-team read gaps, low clarity, weak "why"s;
// - the action plan: the engine's `computeActionPlanKpis` over the handed-off tasks, in weekly
//   hours only (rule 6: team-facing output never carries money; the founder rate is 0);
// - recommended actions: plain sentences, each framed as a suggestion (rule 7).
//
// Every report carries `scoring_version`; a stored report is never recomputed in place.
import { computeActionPlanKpis } from "../engine/actionPlanKpis";
import { getPillarLabel, normalizePillar } from "../engine/pillars";
import { type RespondentInput, toTeamMember } from "../questionnaire/questionnaire";
import { SCORING_VERSION } from "../scoring-version";
import {
  aggregateTeam,
  type MemberMetrics,
  type TeamAggregation,
  type TeamGoalInput,
} from "../team/teamAggregation";

export type Health = "green" | "yellow" | "red" | "grey";
export type GapKind =
  | "participation"
  | "overload"
  | "underuse"
  | "crack"
  | "overlap"
  | "manager_gap"
  | "clarity"
  | "goal";
export type Severity = "high" | "medium" | "low";
export type ActionKind =
  | "hire"
  | "rebalance"
  | "clarify"
  | "fix_crack"
  | "dedupe"
  | "drop"
  | "participation";

export type RoleFinding = {
  participant_id: string;
  name: string;
  role_title: string | null;
  participated: boolean;
  claimed_hours: number;
  capacity_hours: number | null;
  load_index: number | null;
  load_flag: MemberMetrics["load_flag"];
  energy_score: number;
  draining_share: number;
  fit_risk: MemberMetrics["misalignment"]["risk"];
  misaligned_hours: number;
  clarity_score: number | null;
  health: Health;
  top_tasks: { name: string; hours: number }[];
  handoff_hours: number;
};

export type Gap = {
  kind: GapKind;
  severity: Severity;
  title: string;
  detail: string;
  people: string[];
};

export type RecommendedAction = {
  key: string;
  kind: ActionKind;
  title: string;
  detail: string;
  hours_per_week: number | null;
};

export type RoundReport = {
  scoring_version: string;
  participation: {
    invited: number;
    completed: number;
    rate: number;
    low: boolean;
    non_participants: { participant_id: string; name: string }[];
  };
  team: {
    claimed_hours: number;
    capacity_hours: number | null;
    overloaded: number;
    underused: number;
    energy_score: number;
    energizing_hours: number;
    neutral_hours: number;
    draining_hours: number;
    aligned_share: number | null;
  };
  roles: RoleFinding[];
  gaps: Gap[];
  /** The engine's action-plan KPIs over the tasks people said someone else should do (weekly). */
  action_plan: {
    hours_recovered: number;
    workdays_recovered: number;
    tasks_handed_off: number;
  };
  hire_signal: {
    suggested_role: string | null;
    hours_per_week: number;
    by_pillar: {
      pillar: string;
      hours_per_week: number;
      task_count: number;
      top_tasks: string[];
    }[];
  };
  actions: RecommendedAction[];
};

const r1 = (n: number) => Math.round(n * 10) / 10;

/**
 * An area of work as a sentence names it: the engine's pillar label, except its catch-all for
 * admin work ("Non Strategic", and the legacy "Administrative & Internal"), which reads "admin"
 * ("mostly admin", "6 h a week of admin work"). Reports and proposals use it; the engine's own
 * labels (the donut, the dropdown) stay as lifted.
 */
export function areaName(pillar: string | null | undefined): string {
  const key = normalizePillar(pillar)?.key;
  if (key === "Non Strategic" || key === "Administrative & Internal") return "admin";
  return getPillarLabel(pillar);
}

/** Seat health (teamWorkChart.deriveNodeHealth's thresholds): the worst signal wins. */
export function healthOf(m: MemberMetrics): Health {
  if (!m.participated) return "grey";
  const signals: Health[] = [];
  if (m.load_flag === "overloaded") signals.push("red");
  else if (m.load_flag === "underutilized") signals.push("yellow");
  if (m.clarity_score !== null)
    signals.push(m.clarity_score < 40 ? "red" : m.clarity_score < 65 ? "yellow" : "green");
  signals.push(
    m.energy.draining_share >= 0.5 ? "red" : m.energy.draining_share >= 0.3 ? "yellow" : "green",
  );
  if (m.misalignment.risk === "high") signals.push("red");
  else if (m.misalignment.risk === "moderate") signals.push("yellow");
  if (signals.includes("red")) return "red";
  if (signals.includes("yellow")) return "yellow";
  return "green";
}

function handoffHours(m: MemberMetrics): number {
  return r1(m.resolutions.delegate.reduce((s, d) => s + d.hours_per_week, 0));
}

function gapsOf(agg: TeamAggregation): Gap[] {
  const gaps: Gap[] = [];
  const p = agg.participation;
  if (p.low)
    gaps.push({
      kind: "participation",
      severity: "high",
      title: `Only ${p.completed} of ${p.invited} answered (${p.rate}%)`,
      detail:
        "Low participation is itself a finding: the picture below covers the people who answered, not the whole team.",
      people: p.non_participants.map((n) => n.name),
    });
  for (const m of agg.overloaded_members)
    gaps.push({
      kind: "overload",
      severity: m.load_index !== null && m.load_index >= 1.3 ? "high" : "medium",
      title: `${m.name} is over capacity`,
      detail:
        m.load_index !== null && m.capacity_hours !== null
          ? `${r1(m.claimed_hours_per_week)} h of tasks a week against ${m.capacity_hours} h available (${Math.round(m.load_index * 100)}%).`
          : `Describes the week as ${m.load_band ?? "overloaded"}.`,
      people: [m.name],
    });
  for (const m of agg.underutilized_members)
    gaps.push({
      kind: "underuse",
      severity: "low",
      title: `${m.name} has room`,
      detail:
        m.capacity_hours !== null
          ? `${r1(m.claimed_hours_per_week)} h of tasks a week against ${m.capacity_hours} h available.`
          : `Describes the week as ${m.load_band ?? "underloaded"}.`,
      people: [m.name],
    });
  for (const c of agg.crack_clusters.filter((x) => x.shared_by_multiple))
    gaps.push({
      kind: "crack",
      severity: c.member_count >= 3 ? "high" : "medium",
      title: `"${c.label}" falls through the cracks`,
      detail: `Named by ${c.member_count} people.`,
      people: [...new Set(c.mentions.map((x) => x.member_name))],
    });
  for (const o of agg.overlaps)
    gaps.push({
      kind: "overlap",
      severity: o.member_count >= 3 ? "medium" : "low",
      title: `${o.member_count} people do "${o.label}"`,
      detail: `${r1(o.total_hours_per_week)} h a week between them; one owner may be enough.`,
      people: [...new Set(o.activities.map((a) => a.member_name))],
    });
  for (const i of agg.inconsistencies.items)
    gaps.push({
      kind: "manager_gap",
      severity: i.severity === "stark" ? "high" : "medium",
      title: `${i.manager_name} and ${i.report_name} read ${i.kind} differently`,
      detail: i.detail,
      people: [i.manager_name, i.report_name],
    });
  for (const m of agg.members.filter(
    (x) => x.participated && x.clarity_score !== null && x.clarity_score < 50,
  ))
    gaps.push({
      kind: "clarity",
      severity: (m.clarity_score ?? 0) < 35 ? "high" : "medium",
      title: `${m.name}'s role is unclear to them`,
      detail: `Role clarity ${m.clarity_score} of 100.`,
      people: [m.name],
    });
  const g = agg.goalAlignment;
  if (g.enabled && g.drop_candidates.length > 0)
    gaps.push({
      kind: "goal",
      severity: g.aligned_share < 0.5 ? "medium" : "low",
      title: `${r1(g.questionable_hours)} h a week have no clear reason behind them`,
      detail: `Tasks whose "why" is missing or thin: ${g.drop_candidates
        .slice(0, 3)
        .map((d) => `${d.activity_name} (${d.member_name})`)
        .join(", ")}.`,
      people: [...new Set(g.drop_candidates.map((d) => d.member_name))],
    });
  const rank = { high: 0, medium: 1, low: 2 };
  // Participation stays first whatever its severity (rule 3); the rest by severity.
  return gaps.sort((a, b) =>
    a.kind === "participation"
      ? -1
      : b.kind === "participation"
        ? 1
        : rank[a.severity] - rank[b.severity],
  );
}

function actionsOf(agg: TeamAggregation, hoursRecovered: number): RecommendedAction[] {
  const out: RecommendedAction[] = [];
  const p = agg.participation;
  if (p.low)
    out.push({
      key: "participation",
      kind: "participation",
      title: "Get the missing answers before acting on the rest",
      detail: `Remind ${p.non_participants.map((n) => n.name).join(", ")}; the findings firm up above 70% participation.`,
      hours_per_week: null,
    });
  const h = agg.hire_signal;
  if (h.total_hours_per_week >= 4 && h.suggested_role)
    out.push({
      key: "hire",
      kind: "hire",
      title: `Consider ${/^[aeiou]/i.test(h.suggested_role) ? "an" : "a"} ${h.suggested_role} seat`,
      detail: `${r1(h.total_hours_per_week)} h a week of work people say someone else should do, mostly ${h.by_pillar[0] ? areaName(h.by_pillar[0].pillar) : "admin"}. Handing it off recovers about ${r1(hoursRecovered / 8)} working days a week.`,
      hours_per_week: r1(h.total_hours_per_week),
    });
  for (const m of agg.overloaded_members) {
    const give = handoffHours(m);
    out.push({
      key: `rebalance:${m.member_id}`,
      kind: "rebalance",
      title: `Take load off ${m.name}`,
      detail:
        give > 0
          ? `Start with the ${give} h a week ${m.name} already flagged for someone else.`
          : `Look at ${m.top_tasks
              .slice(0, 2)
              .map((t) => t.name)
              .join(" and ")} first.`,
      hours_per_week: give > 0 ? give : null,
    });
  }
  for (const m of agg.members.filter(
    (x) => x.participated && x.clarity_score !== null && x.clarity_score < 50,
  ))
    out.push({
      key: `clarify:${m.member_id}`,
      kind: "clarify",
      title: `Write down what ${m.name}'s seat owns`,
      detail:
        "Name the seat's responsibilities and what success looks like; the proposed chart has a start.",
      hours_per_week: null,
    });
  for (const c of agg.crack_clusters.filter((x) => x.shared_by_multiple).slice(0, 3))
    out.push({
      key: `crack:${c.label}`,
      kind: "fix_crack",
      title: `Give "${c.label}" an owner`,
      detail: `${c.member_count} people named it; make it one seat's responsibility.`,
      hours_per_week: null,
    });
  for (const o of agg.overlaps.slice(0, 2))
    out.push({
      key: `dedupe:${o.label}`,
      kind: "dedupe",
      title: `Decide who owns "${o.label}"`,
      detail: `${o.member_count} people spend ${r1(o.total_hours_per_week)} h a week on it.`,
      hours_per_week: r1(o.total_hours_per_week),
    });
  const g = agg.goalAlignment;
  if (g.enabled && g.drop_candidates.length > 0)
    out.push({
      key: "drop",
      kind: "drop",
      title: "Question the work nobody can tie to the goal",
      detail: `${g.drop_candidates
        .slice(0, 3)
        .map((d) => d.activity_name)
        .join(", ")}: keep, hand off or stop?`,
      hours_per_week: r1(g.drop_candidates.reduce((s, d) => s + d.hours_per_week, 0)),
    });
  return out;
}

export function buildRoundReport(input: {
  respondents: RespondentInput[];
  goal?: TeamGoalInput;
}): RoundReport {
  const members = input.respondents.map(toTeamMember);
  const agg = aggregateTeam({ members, goal: input.goal ?? null });
  // The engine's action-plan KPIs, fed weekly hours (the field name says monthly; the KPI is
  // a sum, so the unit carries through: x-ray.md §1.3) with no rates: hours only.
  const acts = members
    .filter((m) => m.participated)
    .flatMap((m) =>
      m.activities.map((a) => ({
        ...a,
        delegation_plan: {
          rova_tracker: {
            diy_hours_monthly: Number(a.hours_per_week) || 0,
            va_hours_monthly: Number(a.hours_per_week) || 0,
          },
        },
      })),
    );
  const kpis = computeActionPlanKpis(acts, [], 0, {
    team_size_band: null,
    gross_revenue_band: null,
  });
  return {
    scoring_version: SCORING_VERSION,
    participation: {
      invited: agg.participation.invited,
      completed: agg.participation.completed,
      rate: agg.participation.rate,
      low: agg.participation.low,
      non_participants: agg.participation.non_participants.map((n) => ({
        participant_id: n.member_id,
        name: n.name,
      })),
    },
    team: {
      claimed_hours: agg.team.total_claimed_hours_per_week,
      capacity_hours: agg.team.total_capacity_hours_per_week,
      overloaded: agg.team.overloaded_count,
      underused: agg.team.underutilized_count,
      energy_score: agg.team.energy.energy_score,
      energizing_hours: agg.team.energy.energizing_hours,
      neutral_hours: agg.team.energy.neutral_hours,
      draining_hours: agg.team.energy.draining_hours,
      aligned_share: agg.goalAlignment.enabled
        ? r1(agg.goalAlignment.aligned_share * 100) / 100
        : null,
    },
    roles: agg.members.map((m) => ({
      participant_id: m.member_id,
      name: m.name,
      role_title: m.role_title,
      participated: m.participated,
      claimed_hours: r1(m.claimed_hours_per_week),
      capacity_hours: m.capacity_hours,
      load_index: m.load_index === null ? null : Math.round(m.load_index * 100) / 100,
      load_flag: m.load_flag,
      energy_score: m.energy.energy_score,
      draining_share: Math.round(m.energy.draining_share * 100) / 100,
      fit_risk: m.misalignment.risk,
      misaligned_hours: r1(m.misalignment.misaligned_hours),
      clarity_score: m.clarity_score,
      health: healthOf(m),
      top_tasks: m.top_tasks.slice(0, 5).map((t) => ({ name: t.name, hours: r1(t.hours) })),
      handoff_hours: handoffHours(m),
    })),
    gaps: gapsOf(agg),
    action_plan: {
      hours_recovered: r1(kpis.hoursRecovered),
      workdays_recovered: r1(kpis.workdaysRecovered),
      tasks_handed_off: agg.delegate_candidates.length,
    },
    hire_signal: {
      suggested_role: agg.hire_signal.suggested_role,
      hours_per_week: r1(agg.hire_signal.total_hours_per_week),
      by_pillar: agg.hire_signal.by_pillar.map((b) => ({
        pillar: b.pillar,
        hours_per_week: r1(b.hours_per_week),
        task_count: b.task_count,
        top_tasks: b.top_tasks.slice(0, 5),
      })),
    },
    actions: actionsOf(agg, kpis.hoursRecovered),
  };
}
